import { useSelector } from 'react-redux';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { DependencyUsage, dependencyUsageFromDepth, isProjectScope } from '../../../../../shared/issuesV2';
import { useStartGitHubAppInstall } from '../../../../hooks/api/vcs/githubApp/useStartGitHubAppInstall';
import { getErrorMessage } from '../../../../ReactQuery/errors';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import DetailsPanel from '../../../generic/DetailsPanel';
import VulnerabilityDrawerSection from '../../../shared/vulns/Drawer/Section';

import { FossabotConnectionBanner } from './FossabotConnectionBanner';
import { FossabotFixBox } from './FossabotFixBox';
import { FossabotNoFixBox } from './FossabotNoFixBox';
import { FossabotOtherFixesBox } from './FossabotOtherFixesBox';
import { FossabotProjectScopePrompt } from './FossabotProjectScopePrompt';
import { useFossabotConnection } from './useFossabotConnection';
import { useFossabotNoFix } from './useFossabotNoFix';
import { useFossabotPrFlow } from './useFossabotPrFlow';
import type { FossabotPrFlow } from './useFossabotPrFlow';

/** Why Create PR is disabled when the repository is not connected; the banner's Connect button is the CTA. */
const NOT_CONNECTED_REASON = 'Connect fossabot to create PRs';

/** Why Create PR is disabled on a transitive dependency: fossabot only edits the manifest. */
const TRANSITIVE_REASON = 'fossabot can only update direct dependencies right now';

/** The fix cards still render when not connected, in their idle state with Create PR disabled. */
const NOT_CONNECTED_FLOW: FossabotPrFlow = {
  status: 'none',
  vcsUrl: null,
  fossabotUrl: null,
  onCreatePR: () => undefined,
};

/**
 * The fossabot remediation panel, shown for vulnerability issues when the
 * fossabotDepUpgradePRs flag is on and the user can view fossabot upgrades.
 * Renders the collapsible Remediation section with the fossabot connection
 * banner bleeding edge to edge across the panel.
 */
type Props = {
  /** Switches the drawer to the Projects tab, where each affected project links to project scope. */
  onViewProjects: () => void;
};

export const FossabotRemediationPanel: VoidFunctionComponent<Props> = ({ onViewProjects }) => {
  const scope = useSelector(IssuesV2Store.selectors.getScope);
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails);
  const { mutate: startInstall } = useStartGitHubAppInstall();
  const projectLocator = isProjectScope(scope) ? scope.id : '';
  const connection = useFossabotConnection(projectLocator);

  /*
    fossabot upgrades a dependency by editing the manifest, so it can only reach
    direct dependencies. Transitive ones fail in the fossabot service after the
    click, so the button is disabled up front instead. A project we cannot find
    in the issue, or one with an unknown depth, fails open and stays clickable.
  */
  const project = isProjectScope(scope) ? issue?.projects.find(({ id }) => id === scope.id) : null;
  const isTransitive = dependencyUsageFromDepth(project?.depth ?? null) === DependencyUsage.Deep;

  const isConnected = connection.state === 'connected';
  const isLoading = connection.state === 'checking';
  const noFixType = useFossabotNoFix();
  const prFlow = useFossabotPrFlow(isConnected);

  /*
    While the connection status is loading, render no box; it resolves in a
    moment and the banner already covers that state. With no fix to offer,
    neither box has anything to show, so both give way to the unfixable state.
    Not connected, or the status check failed and we cannot tell: the same fix
    cards, with Create PR disabled and the banner's Connect button as the CTA.
  */
  const renderBox = (): ReactNode => {
    if (isLoading) {
      return null;
    }
    if (noFixType !== null) {
      return <FossabotNoFixBox type={noFixType} />;
    }

    const flow = isConnected ? prFlow : NOT_CONNECTED_FLOW;
    if (!flow) {
      return null;
    }

    const getDisabledReason = (): string | undefined => {
      if (!isConnected) {
        return NOT_CONNECTED_REASON;
      }
      return isTransitive ? TRANSITIVE_REASON : undefined;
    };
    const disabledReason = getDisabledReason();

    return (
      <>
        <FossabotFixBox
          status={flow.status}
          vcsUrl={flow.vcsUrl}
          fossabotUrl={flow.fossabotUrl}
          onCreatePR={flow.onCreatePR}
          errorMessage={flow.errorMessage}
          onRetry={flow.onRetry}
          disabledReason={disabledReason}
        />
        <FossabotOtherFixesBox onCreatePR={flow.onCreatePR} disabledReason={disabledReason} />
      </>
    );
  };

  /*
    Matches the sibling sections (Remediation, Header): with no issue in Redux
    there is nothing to remediate, so render nothing rather than an empty card.
  */
  if (!issue) {
    return null;
  }

  /*
    Outside project scope (global / release group) a fossabot PR cannot be
    created (it targets exactly one repository), so the connection banner and
    fix boxes give way to a prompt pointing the user at project scope, regardless
    of connection state.
  */
  return (
    <VulnerabilityDrawerSection>
      <DetailsPanel id="fossabot-remediation" target="Remediation" isUnpadded>
        <div data-testid="fossabot-remediation-panel">
          {isProjectScope(scope) ? (
            <>
              <FossabotConnectionBanner
                state={connection.state}
                errorMessage={getErrorMessage(connection.error, 'Could not check fossabot connection status.')}
                onConnect={() => startInstall('fossabot')}
              />
              {renderBox()}
            </>
          ) : (
            <FossabotProjectScopePrompt onViewProjects={onViewProjects} />
          )}
        </div>
      </DetailsPanel>
    </VulnerabilityDrawerSection>
  );
};
